import * as Sentry from '@sentry/browser'
import { hitMonitorCooldown } from './dedupe'
import { isMonitorReportable } from './filter'
import { applyMonitorScope } from './context'

let isMonitorClientReady = false

// capture 前统一做三件事：
// 1. client 是否已初始化
// 2. 事件是否允许上报
// 3. 是否命中冷却去重
const shouldSkipMonitorCapture = (payload, meta = {}) => {
  if (!isMonitorClientReady) {
    return true
  }

  if (!isMonitorReportable(payload)) {
    return true
  }

  if (hitMonitorCooldown(meta.dedupeKey, meta.cooldownMs)) {
    return true
  }

  return false
}

// 初始化底层监控 SDK。
// 这是 core 层唯一直接接触 @sentry/browser 的入口。
export const initMonitorClient = (options = {}) => {
  const { dsn, beforeSend, ...restOptions } = options

  if (isMonitorClientReady || !dsn) {
    return false
  }

  Sentry.init({
    dsn,
    ...restOptions,
    beforeSend (event, hint) {
      if (!isMonitorReportable(hint?.originalException)) {
        return null
      }

      if (typeof beforeSend === 'function') {
        return beforeSend(event, hint)
      }

      return event
    },
  })

  isMonitorClientReady = true
  return true
}

// 统一上报异常类事件。
// infra/domain 层只需要传 error 和补充 meta，不需要直接接触 Sentry。
export const captureMonitorException = (error, meta = {}) => {
  if (shouldSkipMonitorCapture(error, meta)) {
    return
  }

  Sentry.withScope(scope => {
    applyMonitorScope(scope, meta)
    Sentry.captureException(error)
  })
}

// 统一上报消息类事件，常用于 warning / info。
export const captureMonitorMessage = (message, meta = {}) => {
  if (!message || shouldSkipMonitorCapture(meta.payload, meta)) {
    return
  }

  Sentry.withScope(scope => {
    applyMonitorScope(scope, meta)
    Sentry.captureMessage(String(message))
  })
}

// 暴露 client 是否已准备好，方便外层做状态判断。
export const getMonitorClientReady = () => isMonitorClientReady
